import { COUNTRY_ISO, type CountryIso } from './country';
import type { LanguageIso } from './language';
import { getLocale } from './locale';

export const CURRENCY = {
  CHF: 'CHF',
  DKK: 'kr.',
  SEK: 'kr',
  GBP: '£',
  EUR: '\u20AC'
};

export interface Currency {
  code: string;
  symbol: string;
}

export type GetCurrency = (country: CountryIso) => Currency;
export type GetValueWithCurrency = (value: number, country: CountryIso, language: LanguageIso) => string;
export type GetFormattedValueWithCurrency = (value: number, currencyCode: CountryIso, language: LanguageIso) => string;

/**
 * Retrieves the correct currency object (code + symbol) for a given country.
 *
 * @param country - The ISO code of the country.
 * @returns {Currency} An object with `code` and `symbol` properties for display.
 *
 */
export function getCurrency(country: CountryIso): Currency {
  switch (country) {
    case COUNTRY_ISO.CH:
      return {
        code: 'CHF',
        symbol: CURRENCY.CHF
      };
    case COUNTRY_ISO.DK:
      return {
        code: 'DKK',
        symbol: CURRENCY.DKK
      };
    case COUNTRY_ISO.SE:
      return {
        code: 'SEK',
        symbol: CURRENCY.SEK
      };
    case COUNTRY_ISO.GB:
      return {
        code: 'GBP',
        symbol: CURRENCY.GBP
      };
    default:
      return {
        code: 'EUR',
        symbol: CURRENCY.EUR
      };
  }
}

/**
 * Formats a numeric value with currency symbol, using `Intl.NumberFormat` based on the given country and language.
 * Produces fully localized number formatting, including grouping separators and decimal symbols and a fixed number of
 * 2 decimal places. The currency symbol is included in the formatted string according to the locale's conventions.
 *
 * @param value - The numeric value to format.
 * @param country - The country to determine the locale.
 * @param language - The language/locale for number formatting.
 * @returns A localized and formatted number string without currency symbol.
 */
export function getFormattedValueWithoutCurrency(value: number, country: CountryIso, language: LanguageIso) {
  const locale = getLocale(country, language);
  const currency = getCurrency(country);
  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: 2
  };

  return Intl.NumberFormat(locale, formatOptions).format(value);
}

/**
 * Returns a string combining the value and currency symbol for display,
 * formatted in a country-specific way.
 *
 * @param value - The numeric value to display.
 * @param country - The country for which the currency should be formatted.
 * @returns A string like "€5", "5€", or "£5"
 *
 */
export function getValueWithCurrency(value: number, country: CountryIso, language: LanguageIso): string {
  // Swiss convention requires CHF before the number (CHF 26.95) for all Swiss locales.
  // The fr-CH and it-CH CLDR patterns place CHF after (26.95 CHF), so we format the
  // number separately and prepend the currency code instead of relying on Intl.NumberFormat.
  if (country === COUNTRY_ISO.CH) {
    // Always use de-CH for decimal formatting — Swiss franc prices use a period as decimal
    // separator regardless of the user's language (fr-CH and it-CH use a comma in CLDR).
    const numberString = Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);

    return `${CURRENCY.CHF} ${numberString}`;
  }

  // Modern CLDR data (Node.js 18+) changed de-AT to place € before the number (€ 5,00).
  // Austrian publishing convention places € after, matching de-DE (5,00 €).
  if (country === COUNTRY_ISO.AT) {
    const numberString = Intl.NumberFormat('de-AT', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);

    return `${numberString} ${CURRENCY.EUR}`;
  }

  return getFormattedValueWithoutCurrency(value, country, language);
}

/**
 * Formats a value using `Intl.NumberFormat` based on the given country and language.Produces fully localized currency
 * formatting, including grouping separators and decimal symbols. If there is a locale which has multiple countries using the same
 * language (e.g. de, fr, it, en, nl), the country code is used to determine the correct locale variant.
 *
 * @param value - The numeric value to format.
 * @param country - The country to determine the currency type.
 * @param language - The language/locale for number formatting.
 * @returns A localized and formatted currency string.
 *
 */
export function getFormattedValueWithCurrency(value: number, country: CountryIso, language: LanguageIso) {
  const locale = getLocale(country, language);
  const currency = getCurrency(country);

  return Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.code
  }).format(value);
}
